import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import {
  PLOT_HEIGHT,
  cardState,
  spanClass,
  ticksFor,
  type WidgetBlockProps,
} from '../shared/cardShell';

export type SentimentOverTimeView = {
  /** `Mean sentiment score` (the 0–100 score, not the signed balance) and `Negative share now`. */
  kpis: KPIMetric[];
  rows: ChartRow[];
  xKey: string;
  /** One line per sentiment present in scope. */
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type SentimentOverTimeCardProps = WidgetBlockProps & {
  view?: SentimentOverTimeView;
  /** Opens the conversations behind a point (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/** Figma `849:29599` — the sentiment mix as three lines. */
export function SentimentOverTimeCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: SentimentOverTimeCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Sentiment over time"
      helpText="How the positive, neutral and negative sentiment changes during the selected period."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            xTicksTarget={ticksFor(span)}
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
